/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState } from 'react';
import dayjs, { ManipulateType } from 'dayjs';
import classNames from 'classnames';
import type { Transform } from '@dnd-kit/utilities';
import { CSS } from '@dnd-kit/utilities';
import CardContainer from 'src/components/layouts/CardContainer/CardContainer';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import BarChart from 'src/components/charts/BarChart/BarChart';
import { DashboardBarChartDiagramKpi } from 'src/services/kpi';
import { useNavigate } from 'react-router-dom';
import styles from './DashboardBarChartDiagramKpiCard.module.scss';

export const TabFilterEnum = {
  Entwurf: 'draft',
  Offen: 'open',
  Abgebrochen: 'cancel',
  'Pick & Pack': 'in_progress',
  Verpackt: 'ready_for_shipment',
  Versendet: 'shipped',
  Zugestellt: 'delivered',
  'Nicht auf Lager': 'not_available',
  'In Untersuchung': 'under_investigation',
} as const;

export type TabFilterEnumKey = keyof typeof TabFilterEnum;

const getYAxisMax = (datasets: any[]) => {
  if (!datasets.length) return 0;

  const dataLength = datasets[0].data.length;

  const positionSums = new Array(dataLength).fill(0);

  datasets.forEach(dataset => {
    dataset.data.forEach((value: number, index: number) => {
      positionSums[index] += value;
    });
  });

  return Math.max(...positionSums);
};

export interface DashboardBarChartDiagramKpiCardProps {
  data: DashboardBarChartDiagramKpi;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  setNodeRef?: (node: HTMLElement | null) => void;
  transform?: Transform | null;
  transition?: string;
  title: string;
  actionChildren?: React.ReactNode;
}

const DashboardBarChartDiagramKpiCard: React.FC<DashboardBarChartDiagramKpiCardProps> = ({
  data: apiData,
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  title,
  actionChildren,
}) => {
  const navigate = useNavigate();

  const style = {
    transform: CSS.Transform.toString(transform || null),
    transition,
    touchAction: 'none',
  };

  const datasets = apiData.lines.map(line => ({
    label: line.label,
    data: line.data,
    borderColor: line.color,
    backgroundColor: line.color,
    maxBarThickness: 48,
  }));

  const data = {
    labels: apiData.yLabels,
    datasets,
  };

  const legendData = apiData.lines.map(line => ({
    label: line.label,
    color: line.color,
    total_count: line.data.reduce((sum, val) => sum + val, 0),
  }));

  const labelToFilter = new Map(apiData.lines.filter(line => line.key).map(line => [line.label, line.key as string]));

  const handleBarChartClick = (label: string, timestamp: string, index: number) => {
    const filter = labelToFilter.get(label) ?? TabFilterEnum[label as TabFilterEnumKey];
    if (!filter) return;

    let isoDate: string;
    let isoDateNextDay: string;
    const bucketDate = apiData.bucket_dates?.[index];
    if (bucketDate && apiData.bucket) {
      const start = dayjs(bucketDate);
      const width: ManipulateType = apiData.bucket;
      isoDate = start.format('YYYY-MM-DD');
      isoDateNextDay = start.add(1, width).format('YYYY-MM-DD');
    } else {
      const [dayStr, monthStr, yearStr] = timestamp.split('.');
      const day = parseInt(dayStr, 10);
      const month = parseInt(monthStr, 10);
      const year = parseInt(yearStr, 10);
      isoDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const next = new Date(year, month - 1, day + 1);
      isoDateNextDay = `${next.getFullYear()}-${(next.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${next.getDate().toString().padStart(2, '0')}`;
    }

    const domain = JSON.stringify([
      ['fulfillment_state', '=', filter],
      ['date_order', '>=', isoDate],
      ['date_order', '<', isoDateNextDay],
    ]);

    navigate('/outbound', {
      state: {
        preselectedDomain: domain,
        preselectedTab: filter,
      },
    });
  };

  return (
    <CardContainer
      className={classNames(styles.root)}
      ref={setNodeRef}
      style={style}
      label={title || apiData.title}
      attributes={attributes}
      listeners={listeners}
      actionChildren={actionChildren}>
      <div className="flex gap-x-60">
        <div className="w-full min-w-0">
          <BarChart
            data={data}
            options={{
              scales: {
                y: {
                  min: 0,
                  max: getYAxisMax(data.datasets),
                },
              },
            }}
            onBarClick={(label, timestamp, index) => {
              handleBarChartClick(label, timestamp, index);
            }}
          />
        </div>
        <div className="flex flex-col gap-y-20 min-w-80">
          {legendData.map((item, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx} className="col-span-2 lg:col-span-1 flex justify-between items-center w-full typo-text-14">
              <div className="flex items-center gap-x-6">
                <span className="w-3 h-3 inline-block rounded-[0.2rem]" style={{ backgroundColor: item.color }} />
                <div className="label typo-text-14--bold">{item.label}</div>
              </div>
              <div className="quantity typo-text-14 text-faded">{item.total_count}</div>
            </div>
          ))}
        </div>
      </div>
    </CardContainer>
  );
};

export default DashboardBarChartDiagramKpiCard;
